import styles from './customerTypeForm.module.css'
import { useMemo, useState } from "react"
import { CustomerType, ProductYield } from "../../../state/types"
import { AdjustmentType } from "../../../../operations"
import { useActions } from "./actions"
import { PanelWrapper, Data, FieldType, FieldValue, FormField, option, useGetCrumbs, O, ModalId, useUIActions } from "../../../../../features/ui"
import { DataTypeForm, Labels, useDescribeHooks } from "../../../../../features/data-types"
import { useNavigate } from "react-router-dom"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { TableName } from "../../../../../tables"
import { PriceListProductsTable } from "../../customer-type/price-list-products-table/priceListProductsTable"
import { ProductYieldFormModal } from "../../customer-type/product-yield-form-modal/productYieldFormModal"
import { ConfirmBaseChangeModal } from "./confirmBaseChangeModal"
import { Product } from "../../../../products"
import { ExcludedProductsFormModal } from "../../customer-type/excluded-products-form-modal/excludedProductsFormModal"
import { ExcludedProductsTable } from "../../customer-type/excluded-products-table/excludedProductsTable"
import { useCustomerTypeLoaders } from "../../.."
import { isSameYield } from "../customerTypeUtils"

export const CustomerTypeForm = () => {
    const { dataType } = useRoutesHooks().parseUrl()
    const customerType = dataType as O<CustomerType>
    const createMode = !customerType

    const defaultType = customerType && customerType?.yield < 0 ? AdjustmentType.DISCOUNT : AdjustmentType.SURCHARGE
    const [adjustmentType, setAdjustmentType] = useState<O<AdjustmentType>>(defaultType)
    const [customerTypeYield, setCustomerTypeYield] = useState<number>(customerType?.yield || 0)
    const [productYields, setProductYields] = useState<ProductYield[]>(customerType?.productYields || [])
    const [currentProductYield, setCurrentProductYield] = useState<ProductYield>()
    const [baseCustomerTypeId, setBaseCustomerTypeId] = useState<O<string>>(customerType?.baseCustomerTypeId)
    const [pendingBaseId, setPendingBaseId] = useState<O<string>>(undefined);

    const { findAllCustomerTypes } = useCustomerTypeLoaders()() 
    const allCustomerTypes = findAllCustomerTypes() 

    const baseListOptions = allCustomerTypes
        .filter((ct: CustomerType) => ct.id !== customerType?.id && (!ct.archived || ct.id === customerType?.baseCustomerTypeId))
        .map((ct: CustomerType) => option(ct.id, ct.name))

    const navigate = useNavigate()
    const { describe } = useDescribeHooks()

    const { submitCustomerType } = useActions()
    const { openModal } = useUIActions()

    const excludedYields = useMemo(() => productYields.filter(y => y.isExcluded), [productYields])

    const customerTypeLabel = customerType ? `: ${describe(TableName.CUSTOMER_TYPES, customerType)}` : ''
    const title = `${createMode ? 'Crear' : 'Editar'} ${customerTypeLabel || 'Lista de Precios'}`
    const crumbs = useGetCrumbs(
        'Listas de Precios',
        RoutePath.CUSTOMER_TYPES,
        customerType?.name,
        customerType?.id,
        createMode ? 'Crear' : 'Editar'
    )

    const typeOptions = Object.values(AdjustmentType).map(value => option(value, Labels.adjustmentTypeLabel(value)))
    
    const filterProducts = (product: Product) => 
        currentProductYield?.productId === product.id || 
        !productYields.map(productYield => productYield.productId).includes(product.id)

    const onAdjustmentTypeChange = (value?: FieldValue) => {
        const newType = value as O<AdjustmentType>
        setAdjustmentType(newType)
        const yieldAbsoluteValue = Math.abs(customerTypeYield)
        setCustomerTypeYield(newType === AdjustmentType.DISCOUNT ? -yieldAbsoluteValue : yieldAbsoluteValue)
    }

    const onYieldChange = (value?: FieldValue) => {
        const newYield = (value || 0) as number
        setCustomerTypeYield(adjustmentType === AdjustmentType.DISCOUNT && newYield !== 0 ? -newYield : newYield)
    }

    const onEditProductYield = (productId: string) => {
        const productYield = productYields.find(productYield => productYield.productId === productId)
        setCurrentProductYield(productYield || { productId, yield: customerTypeYield } as ProductYield)
        openModal(ModalId.PRODUCT_YIELD_FORM)
    }

    const onChangeProductYield = (productId: string, newYield: number) => {
        onSaveProductYield({ productId, yield: newYield })
    }

    const onCreateExcluded = () => {
        setCurrentProductYield(undefined)
        openModal(ModalId.EXCLUDED_PRODUCTS_FORM)
    }

    const onRemoveProductYield = (productId: string) => {
        const newProductYields = productYields.filter(productYield => productYield.productId !== productId)
        setProductYields(newProductYields)
    }

    const applyBaseListChange = (newBaseId: O<string>) => {
        setBaseCustomerTypeId(newBaseId)
        
        if (newBaseId) {
            const baseList = allCustomerTypes.find(ct => ct.id === newBaseId)
            
            const currentSpecificAdjustments = productYields.filter(py => !py.isExcluded)
            
            const newExclusions = baseList?.productYields
                .filter(py => py.isExcluded)
                .map(py => ({ ...py, yield: 0 })) || []

            setProductYields([...currentSpecificAdjustments, ...newExclusions])
        }
        setPendingBaseId(undefined)
    }

    const onBaseListChange = (val?: FieldValue) => {
        const newBaseId = val as string
        
        if (productYields.length > 0) {
            setPendingBaseId(newBaseId)
            openModal(ModalId.CONFIRM_BASE_CHANGE)
        } else {
            applyBaseListChange(newBaseId)
        }
    }
    
    const fields: FormField[] = [
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Nombre',
            className: styles.compactField
        },
        {
            name: 'baseCustomerTypeId',
            type: FieldType.SELECT,
            label: 'Lista Base',
            hint: 'Los ajustes se calcularán en base a esta lista de precios.',
            options: baseListOptions,
            defaultValue: baseCustomerTypeId,
            optional: true,
            className: styles.compactField,
            onChange: onBaseListChange
        },
        {
            name: 'adjustmentType',
            type: FieldType.SELECT,
            defaultValue: adjustmentType,
            options: typeOptions,
            label: 'Tipo de Ajuste',
            className: styles.compactField,
            onChange: onAdjustmentTypeChange
        },
        {
            name: 'customerTypeYield',
            type: FieldType.PERCENTAGE,
            defaultValue: Math.abs(customerTypeYield),
            label: 'Ajuste General',
            onChange: onYieldChange
        },
        {
            name: 'productYields',
            type: FieldType.TABLE,
            table: TableName.PRODUCTS,
            disabled: true,
            render: () => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
                    <PriceListProductsTable
                        productYields={productYields}
                        generalYield={customerTypeYield}
                        baseCustomerTypeId={baseCustomerTypeId}
                        onChangeYield={onChangeProductYield}
                        onResetYield={onRemoveProductYield}
                        onEditItem={onEditProductYield}
                    />
                    
                    <ExcludedProductsTable
                        items={excludedYields}
                        onCreateItem={onCreateExcluded}
                        onRemoveItem={onRemoveProductYield}
                    />
                </div>
            )
        }
    ]

    const onSubmit = (customerTypeData: Data) => {
        const finalData = { ...customerTypeData, baseCustomerTypeId }
        // Solo se guardan los ajustes por producto que difieren del general
        const finalProductYields = productYields.filter(py => py.isExcluded || !isSameYield(py.yield, customerTypeYield))
        submitCustomerType(finalData, customerTypeYield, finalProductYields, createMode)
    }

    const onCancel = () => navigate(-1)

    const onSaveProductYield = (newProductYield: Partial<ProductYield>) => {
        const exists = productYields.some(py => py.productId === newProductYield.productId);
        
        const newProductYields = exists ?
            productYields.map(py => py.productId === newProductYield.productId ? { ...py, ...newProductYield } : py) :
            [newProductYield as ProductYield, ...productYields];
            
        setProductYields(newProductYields)
    }

    return (
        <PanelWrapper title={title} crumbs={crumbs} wide>
            <DataTypeForm
               formId="customer-type-form"
               fields={fields}
               createMode={createMode}
               onSubmit={onSubmit}
               onCancel={onCancel}
            />
            
            <ProductYieldFormModal
                defaultData={currentProductYield}
                baseCustomerTypeId={baseCustomerTypeId}
                filterProducts={filterProducts}
                onSubmit={onSaveProductYield}
            />

            <ExcludedProductsFormModal 
                filterProducts={filterProducts}
                onSubmit={onSaveProductYield}
            />

            <ConfirmBaseChangeModal 
                onConfirm={() => applyBaseListChange(pendingBaseId)}
                onCancel={() => setPendingBaseId(undefined)}
            />

        </PanelWrapper>
    )
}
